import React, { useEffect, useState } from 'react';
import { Button, Card, Modal, Popconfirm, Space, Spin, Tag, Typography, Upload } from 'antd';
import {
    DeleteOutlined,
    DownloadOutlined,
    EyeOutlined,
    FilePdfOutlined,
    InboxOutlined,
    PaperClipOutlined,
    UploadOutlined
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { toast } from 'react-toastify';
import { urlAdmin } from '../../../constants';
import { layDsTaiLieuLuongKhenThuongUngHo, luuTaiLieuLuongKhenThuongUngHo } from '../../../services/luong';

const { Text } = Typography;
const { Dragger } = Upload;

interface DinhKemFilePDFProps {
    idLuongKhenThuongUngHo: number;
}

interface TaiLieuItem {
    id: number;
    idTaiLieu: number;
    tenTep: string;
    duongDan: string;
    dinhDang: string;
}

const DinhKemFilePDF: React.FC<DinhKemFilePDFProps> = ({ idLuongKhenThuongUngHo }) => {
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [dsTaiLieu, setDsTaiLieu] = useState<TaiLieuItem[]>([]);
    const [fileList, setFileList] = useState<UploadFile[]>([]);

    const loadData = async () => {
        if (!idLuongKhenThuongUngHo || idLuongKhenThuongUngHo <= 0) {
            setDsTaiLieu([]);
            return;
        }
        try {
            setLoading(true);
            const data = await layDsTaiLieuLuongKhenThuongUngHo(idLuongKhenThuongUngHo);
            setDsTaiLieu(data || []);
        } catch (err: any) {
            toast.error('Lỗi tải danh sách tài liệu đính kèm: ' + (err.message || err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [idLuongKhenThuongUngHo]);

    const handleUpload = async () => {
        if (!idLuongKhenThuongUngHo || idLuongKhenThuongUngHo <= 0) {
            toast.warning('Vui lòng lưu bản ghi trước khi đính kèm tài liệu!');
            return;
        }

        const validFiles: File[] = fileList.map((f: any) => {
            if (f.originFileObj) return f.originFileObj;
            return f as File;
        }).filter((f: any) => f && f.name);

        if (validFiles.length === 0) {
            toast.warning('Vui lòng chọn ít nhất một tệp PDF!');
            return;
        }

        try {
            setUploading(true);
            await luuTaiLieuLuongKhenThuongUngHo({
                idLuongKhenThuongUngHo,
                files: validFiles
            });
            toast.success(`Đã tải lên thành công ${validFiles.length} tệp tài liệu PDF!`);
            setFileList([]);
            setIsModalOpen(false);
            await loadData();
        } catch (err: any) {
            toast.error('Lỗi khi tải tệp lên: ' + (err.message || err));
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteFile = async (item: TaiLieuItem) => {
        try {
            setLoading(true);
            await luuTaiLieuLuongKhenThuongUngHo({
                idLuongKhenThuongUngHo,
                idsTaiLieuXoa: [item.idTaiLieu]
            });
            toast.success('Đã xóa tệp đính kèm thành công!');
            await loadData();
        } catch (err: any) {
            toast.error('Lỗi khi xóa tệp: ' + (err.message || err));
        } finally {
            setLoading(false);
        }
    };

    const uploadProps: UploadProps = {
        name: 'files',
        multiple: true,
        accept: '.pdf,application/pdf',
        fileList: fileList,
        onRemove: (file) => {
            setFileList((prev) => prev.filter((item) => item.uid !== file.uid && item.name !== file.name));
        },
        beforeUpload: (file) => {
            const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
            if (!isPdf) {
                toast.warning(`${file.name} không phải là định dạng PDF!`);
                return Upload.LIST_IGNORE;
            }
            if (file.size > 25 * 1024 * 1024) {
                toast.warning(`Tệp ${file.name} vượt quá dung lượng cho phép (tối đa 25MB)!`);
                return Upload.LIST_IGNORE;
            }
            setFileList((prev) => {
                if (prev.some(item => item.uid === file.uid || (item.name === file.name && item.size === file.size))) {
                    return prev;
                }
                return [...prev, file];
            });
            return false;
        },
    };

    const getFileUrl = (duongDan: string) => {
        if (!duongDan) return '';
        const cleanPath = duongDan.replace(/\\/g, '/');
        if (cleanPath.startsWith('http')) return cleanPath;
        return (urlAdmin || '') + cleanPath;
    };

    if (!idLuongKhenThuongUngHo || idLuongKhenThuongUngHo <= 0) {
        return null;
    }

    return (
        <>
            <Card
                className="card card-body mb-3"
                style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '12px 16px' }}
                bodyStyle={{ padding: 0 }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: dsTaiLieu.length > 0 ? 12 : 0 }}>
                    <Space size={8}>
                        <PaperClipOutlined style={{ color: '#1890ff', fontSize: 16 }} />
                        <span style={{ fontWeight: 600, fontSize: 14, color: '#262626' }}>Tài liệu đính kèm (PDF)</span>
                        <Tag color={dsTaiLieu.length > 0 ? "blue" : "default"} style={{ borderRadius: 10, margin: 0, fontWeight: 500 }}>
                            {dsTaiLieu.length} tệp
                        </Tag>
                    </Space>
                    <Button
                        type="primary"
                        ghost
                        size="small"
                        icon={<UploadOutlined />}
                        onClick={() => {
                            setFileList([]);
                            setIsModalOpen(true);
                        }}
                    >
                        Đính kèm tệp PDF
                    </Button>
                </div>

                <Spin spinning={loading}>
                    {dsTaiLieu.length === 0 ? (
                        <div style={{ color: '#8c8c8c', fontSize: 13, padding: '4px 0' }}>
                            Chưa có tài liệu đính kèm nào. Nhấn <b>"Đính kèm tệp PDF"</b> để thêm tài liệu.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                            {dsTaiLieu.map((item) => {
                                const fileUrl = getFileUrl(item.duongDan);
                                return (
                                    <div
                                        key={item.idTaiLieu}
                                        style={{
                                            border: '1px solid #e8e8e8',
                                            borderRadius: 6,
                                            padding: '6px 12px',
                                            background: '#fafafa',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            maxWidth: '360px',
                                            minWidth: '240px',
                                            flex: '1 1 240px'
                                        }}
                                    >
                                        <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 20, flexShrink: 0 }} />
                                        <div
                                            title={item.tenTep}
                                            style={{
                                                flex: 1,
                                                minWidth: 0,
                                                fontSize: 13,
                                                fontWeight: 500,
                                                color: '#262626',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {item.tenTep}
                                        </div>
                                        <Space size={2} style={{ flexShrink: 0 }}>
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<EyeOutlined style={{ color: '#1890ff' }} />}
                                                title="Xem trực tuyến"
                                                onClick={() => window.open(fileUrl, '_blank')}
                                            />
                                            <a href={fileUrl} download={item.tenTep} target="_blank" rel="noreferrer">
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<DownloadOutlined style={{ color: '#52c41a' }} />}
                                                    title="Tải về máy"
                                                />
                                            </a>
                                            <Popconfirm
                                                title="Xác nhận xóa tài liệu này?"
                                                okText="Xóa"
                                                cancelText="Hủy"
                                                okButtonProps={{ danger: true }}
                                                onConfirm={() => handleDeleteFile(item)}
                                            >
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
                                                    title="Xóa tài liệu"
                                                />
                                            </Popconfirm>
                                        </Space>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Spin>
            </Card>

            {/* Modal đính kèm tệp */}
            <Modal
                title={
                    <Space>
                        <FilePdfOutlined style={{ color: '#ff4d4f' }} />
                        <span>Đính kèm tài liệu PDF</span>
                    </Space>
                }
                open={isModalOpen}
                onCancel={() => {
                    if (!uploading) {
                        setIsModalOpen(false);
                        setFileList([]);
                    }
                }}
                footer={[
                    <Button key="cancel" disabled={uploading} onClick={() => { setIsModalOpen(false); setFileList([]); }}>
                        Hủy
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={uploading}
                        disabled={fileList.length === 0}
                        icon={<UploadOutlined />}
                        onClick={handleUpload}
                    >
                        Tải lên ({fileList.length})
                    </Button>
                ]}
                destroyOnClose
                centered
            >
                <div style={{ padding: '8px 0' }}>
                    <Dragger {...uploadProps} style={{ padding: '20px 16px', background: '#fafafa', borderRadius: 8 }}>
                        <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                            <InboxOutlined style={{ color: '#ff4d4f', fontSize: 40 }} />
                        </p>
                        <p className="ant-upload-text" style={{ fontSize: 14, fontWeight: 500, margin: '0 0 4px 0' }}>
                            Nhấp hoặc kéo thả tệp PDF vào đây
                        </p>
                        <p className="ant-upload-hint" style={{ fontSize: 12, color: '#8c8c8c', margin: 0 }}>
                            Chỉ chấp nhận tệp định dạng .pdf (tối đa 25MB/tệp). Hỗ trợ chọn nhiều tệp cùng lúc.
                        </p>
                    </Dragger>
                </div>
            </Modal>
        </>
    );
};

export default DinhKemFilePDF;
